import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BellRingingIcon as BellRinging, CheckCircleIcon as CheckCircle, XCircleIcon as XCircle, DeviceMobileIcon as DeviceMobile, ArrowClockwiseIcon as ArrowClockwise } from "@phosphor-icons/react";

function StatusPill({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-semibold ${ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
      {ok ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} weight="fill" />}
      {label}
    </span>
  );
}

const REASON_HELP = {
  "apns-not-configured": "Apple push keys are missing on this server — the APNS secrets did not reach this environment.",
  "fcm-not-configured": "Firebase key is missing on this server (Android delivery).",
  BadDeviceToken: "Apple rejected this device token — the device needs to reopen the app to register a fresh token.",
  Unregistered: "This device uninstalled the app or its token expired. It re-registers on next app open.",
  DeviceTokenNotForTopic: "Token belongs to a different app bundle ID — check APNS_BUNDLE_ID.",
  TopicDisallowed: "The push key isn't allowed to send to this bundle ID.",
  InvalidProviderToken: "Apple rejected the .p8 key credentials (key ID / team ID mismatch).",
};

export default function AdminPushTest() {
  const [overview, setOverview] = useState(null);
  const [mine, setMine] = useState(null);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [ov, ts] = await Promise.all([api.get("/push/admin/overview"), api.get("/push/token-status")]);
      setOverview(ov.data);
      setMine(ts.data);
    } catch (e) {
      toast.error("Could not load push status");
    }
  };
  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    setBusy(true);
    setResults(null);
    try {
      const r = await api.post("/push/test");
      setResults(r.data.results);
      if (r.data.sent > 0) toast.success(`Sent to ${r.data.sent} device(s) — check your phone!`);
      else toast.error("No device received the push — see reasons below.");
    } catch (e) {
      toast.error("Test failed to run");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="admin-push-test-page">
      <p className="text-[11px] uppercase tracking-[0.2em] text-[#9A9A9A] font-semibold mb-2">Admin · Diagnostics</p>
      <h1 className="font-spectral text-4xl text-ink mb-2">Push notifications</h1>
      <p className="text-[14px] text-[#6B6B6B] mb-8">
        Check delivery configuration, see registered devices, and send yourself a live test.
      </p>

      {overview && (
        <div className="rounded-xl border border-black/10 p-5 mb-6" data-testid="push-config-status">
          <h2 className="text-lg font-bold text-ink mb-3">Server configuration</h2>
          <div className="flex flex-wrap gap-2">
            <StatusPill ok={overview.apns_configured} label={overview.apns_configured ? "Apple (iOS) delivery configured" : "Apple keys MISSING on this server"} />
            <StatusPill ok={overview.fcm_configured} label={overview.fcm_configured ? "Firebase (Android) delivery configured" : "Firebase key MISSING on this server"} />
          </div>
        </div>
      )}

      <div className="rounded-xl border border-black/10 p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-ink">Registered devices {overview ? `(${overview.total_devices})` : ""}</h2>
          <Button variant="outline" size="sm" className="rounded-full" onClick={load} data-testid="push-refresh-btn">
            <ArrowClockwise size={14} className="mr-1" /> Refresh
          </Button>
        </div>
        {overview?.devices?.length ? (
          <div className="space-y-2">
            {overview.devices.map((d, i) => (
              <div key={i} className="flex items-center gap-3 text-[13px] text-[#3F3F3F]">
                <DeviceMobile size={15} weight="duotone" className="text-ink shrink-0" />
                <span className="font-semibold">{d.email}</span>
                <span className="uppercase text-[10.5px] tracking-wide bg-black/5 rounded px-1.5 py-0.5">{d.platform}</span>
                <span className="text-[#9A9A9A]">{d.updated_at ? new Date(d.updated_at).toLocaleString() : ""}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[13px] text-[#6B6B6B]" data-testid="push-no-devices">
            No devices registered yet. Open the mobile app, log in, and allow notifications — then hit Refresh.
          </p>
        )}
      </div>

      {overview?.recent_errors?.length ? (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-5 mb-6" data-testid="push-recent-errors">
          <h2 className="text-lg font-bold text-ink mb-2">Device registration errors</h2>
          <div className="space-y-1.5">
            {overview.recent_errors.map((e, i) => (
              <p key={i} className="text-[12.5px] text-[#6B4E00]">
                <span className="uppercase font-bold">{e.platform}</span> · {e.created_at ? new Date(e.created_at).toLocaleString() : ""} — {e.error}
              </p>
            ))}
          </div>
        </div>
      ) : null}

      <div className="rounded-xl border border-black/10 p-5">
        <h2 className="text-lg font-bold text-ink mb-1">Send a test to my devices</h2>
        <p className="text-[13px] text-[#6B6B6B] mb-4">
          Sends a real notification to every device registered to <strong>your</strong> account
          ({mine?.count ?? 0} device{(mine?.count ?? 0) === 1 ? "" : "s"}).
        </p>
        <Button
          onClick={sendTest}
          disabled={busy}
          className="rounded-full bg-ink hover:bg-crimson text-white h-11 px-6"
          data-testid="push-send-test-btn"
        >
          <BellRinging size={16} weight="duotone" className="mr-2" />
          {busy ? "Sending…" : "Send test notification"}
        </Button>

        {results && (
          <div className="mt-5 space-y-3" data-testid="push-test-results">
            {results.length === 0 && (
              <p className="text-[13px] text-red-700">
                Your account has no registered devices. Open the app on your phone, log in, allow notifications, then Refresh and try again.
              </p>
            )}
            {results.map((r, i) => (
              <div key={i} className={`rounded-lg p-3 text-[13px] ${r.ok ? "bg-emerald-50" : "bg-red-50"}`}>
                <div className="flex items-center gap-2 font-semibold">
                  {r.ok ? <CheckCircle size={15} weight="fill" className="text-emerald-600" /> : <XCircle size={15} weight="fill" className="text-red-600" />}
                  {r.platform.toUpperCase()} device (…{r.token_tail}) — {r.ok ? "delivered to Apple/Google" : r.reason}
                </div>
                {!r.ok && REASON_HELP[r.reason] && (
                  <p className="mt-1 text-[#6B6B6B]">{REASON_HELP[r.reason]}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
